import React, { useState, useEffect, useRef, useCallback } from 'react';

function Icon({ name, style, className = '' }) {
  return <span className={`material-symbols-outlined ${className}`} style={style}>{name}</span>;
}

function Spinner() {
  return (
    <div className="spinner-wrap">
      <div className="spinner" />
      <span style={{ fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Loading...</span>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="skeleton-card">
      <div className="skeleton-card-image" />
      <div className="skeleton-card-body">
        <div className="skeleton-line skeleton-line-medium" style={{ height: 16 }} />
        <div className="skeleton-line skeleton-line-short" style={{ height: 10 }} />
        <div className="skeleton-spec-grid">
          <div className="skeleton-spec-cell"><div className="skeleton-line skeleton-line-short" /><div className="skeleton-line skeleton-line-medium" /></div>
          <div className="skeleton-spec-cell"><div className="skeleton-line skeleton-line-short" /><div className="skeleton-line skeleton-line-medium" /></div>
          <div className="skeleton-spec-cell"><div className="skeleton-line skeleton-line-short" /><div className="skeleton-line skeleton-line-medium" /></div>
          <div className="skeleton-spec-cell"><div className="skeleton-line skeleton-line-short" /><div className="skeleton-line skeleton-line-medium" /></div>
        </div>
      </div>
    </div>
  );
}

function SkeletonGrid({ count = 6 }) {
  return (
    <div className="equipment-grid">
      {Array.from({ length: count }, (_, i) => <SkeletonCard key={i} />)}
    </div>
  );
}

function ErrorBanner({ message }) {
  if (!message) return null;
  return (
    <div className="error-banner">
      <Icon name="error" />
      {message}
    </div>
  );
}

function Modal({ title, onClose, onSubmit, submitting, children }) {
  // close on backdrop click
  const backdropRef = useRef(null);
  function handleBackdrop(e) {
    if (e.target === backdropRef.current) onClose();
  }
  // close on Escape
  useEffect(() => {
    function handler(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="modal-backdrop" ref={backdropRef} onClick={handleBackdrop}>
      <div className="modal animate-in" role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <div className="modal-header">
          <h2 className="modal-title" id="modal-title">{title}</h2>
          <button className="btn-icon" onClick={onClose} aria-label="Close modal">
            <Icon name="close" />
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={onSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ParticleCanvas() {
  const canvasRef = React.useRef(null);
  const animRef = React.useRef(null);
  const particlesRef = React.useRef([]);
  const mouseRef = React.useRef({ x: -1000, y: -1000, active: false });

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const PARTICLE_COUNT = 45;
    const CONNECTION_DIST = 120;
    const SPEED_RANGE = [0.15, 0.4];

    function resize() {
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width;
      canvas.height = rect.height;
    }

    function initParticles() {
      particlesRef.current = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particlesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 2 * (SPEED_RANGE[0] + Math.random() * (SPEED_RANGE[1] - SPEED_RANGE[0])),
          vy: (Math.random() - 0.5) * 2 * (SPEED_RANGE[0] + Math.random() * (SPEED_RANGE[1] - SPEED_RANGE[0])),
          r: 1 + Math.random() * 1.2,
        });
      }
    }

    function draw() {
      if (document.hidden) { animRef.current = requestAnimationFrame(draw); return; }
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const particles = particlesRef.current;

      // Update positions
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        // Mouse repulsion
        if (mouseRef.current.active) {
          const mx = mouseRef.current.x;
          const my = mouseRef.current.y;
          const mdx = p.x - mx;
          const mdy = p.y - my;
          const mDist = Math.sqrt(mdx * mdx + mdy * mdy);
          const REPEL_DIST = 150;
          if (mDist < REPEL_DIST && mDist > 0) {
            const force = (1 - mDist / REPEL_DIST) * 2;
            p.x += (mdx / mDist) * force;
            p.y += (mdy / mDist) * force;
          }
        }
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      }

      // Draw connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const opacity = 0.05 * (1 - dist / CONNECTION_DIST);
            ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
            ctx.lineWidth = 0.5;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw connections to mouse
      if (mouseRef.current.active) {
        const mx = mouseRef.current.x;
        const my = mouseRef.current.y;
        for (const p of particles) {
          const dx = p.x - mx;
          const dy = p.y - my;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST * 1.5) {
            const opacity = 0.08 * (1 - dist / (CONNECTION_DIST * 1.5));
            ctx.strokeStyle = `rgba(252, 148, 48, ${opacity})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(mx, my);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.12)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      animRef.current = requestAnimationFrame(draw);
    }

    function handleMouseMove(e) {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current.x = e.clientX - rect.left;
      mouseRef.current.y = e.clientY - rect.top;
      mouseRef.current.active = true;
    }

    function handleMouseLeave() {
      mouseRef.current.active = false;
      mouseRef.current.x = -1000;
      mouseRef.current.y = -1000;
    }

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mouseleave', handleMouseLeave);

    resize();
    initParticles();
    draw();

    window.addEventListener('resize', () => { resize(); initParticles(); });
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return React.createElement('canvas', {
    ref: canvasRef,
    style: {
      position: 'absolute',
      inset: 0,
      width: '100%',
      height: '100%',
      zIndex: 1,
      pointerEvents: 'auto',
    }
  });
}

function AdBanner({ slot, format, pubId }) {
  if (!pubId || !slot) return null;

  React.useEffect(() => {
    try { (window.adsbygoogle = window.adsbygoogle || []).push({}); } catch {}
  }, []);

  return (
    <div style={{ margin: '24px 0', textAlign: 'center', minHeight: 90 }}>
      <ins className="adsbygoogle"
        style={{ display: 'block' }}
        data-ad-client={pubId}
        data-ad-slot={slot}
        data-ad-format={format || 'auto'}
        data-full-width-responsive="true"
      />
    </div>
  );
}

export { Icon, Spinner, SkeletonCard, SkeletonGrid, ErrorBanner, Modal, ParticleCanvas, AdBanner };
